import { useEffect, useState, useRef } from "react";
import { Package, Trash2, PlusCircle, Save, X, Layers } from "lucide-react";
import Swal from "sweetalert2";
import api from "../services/api";

export default function PurchaseForm({ onSaved, onCancel }) {
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);

  const [supplierId, setSupplierId] = useState("");
  const [fechaCompra, setFechaCompra] = useState("");
  const [numeroFactura, setNumeroFactura] = useState("");
  const [details, setDetails] = useState([]);

  // 🆕 Búsqueda por línea: { [index]: { search, results } }
  const [lineSearch, setLineSearch] = useState({});
  const inputRefs = useRef({});

  useEffect(() => {
    fetchSuppliers();
    fetchProducts();
  }, []);

  const fetchSuppliers = async () => {
    try {
      const res = await api.get("/suppliers");
      setSuppliers(res.data?.data || res.data || []);
    } catch {
      setSuppliers([]);
    }
  };

  const fetchProducts = async () => {
    try {
      const res = await api.get("/products");
      setProducts(res.data?.data || res.data || []);
    } catch {
      setProducts([]);
    }
  };

  /* =========================
     DETALLES
  ========================== */
  const addDetail = () => {
    const newIndex = details.length;
    setDetails(prev => [
      ...prev,
      {
        product_id: "",
        presentation_id: null,
        cantidad: 1,
        precio_unitario: 0,
        productData: null
      },
    ]);
    // Foco automático al nuevo input
    setTimeout(() => inputRefs.current[newIndex]?.focus(), 50);
  };

  const updateDetail = (index, field, value) => {
    const updated = [...details];
    updated[index][field] = value;
    if (field === "product_id") {
      const selectedProduct = products.find(p => p.id == value);
      updated[index].productData = selectedProduct;
      updated[index].presentation_id = null;
    }
    setDetails(updated);
  };

  const removeDetail = (index) => {
    setDetails(details.filter((_, i) => i !== index));
    setLineSearch(prev => {
      const copy = { ...prev };
      delete copy[index];
      return copy;
    });
  };

  /* =========================
     🆕 BÚSQUEDA POR LÍNEA (igual que PostForm)
  ========================== */
  const handleLineSearchChange = (index, value) => {
    const q = value.toLowerCase();

    const results = value
      ? products.filter(p =>
          p.codigo?.toLowerCase().includes(q) ||
          p.nombre?.toLowerCase().includes(q)
        ).slice(0, 6)
      : [];

    setLineSearch(prev => ({
      ...prev,
      [index]: { search: value, results }
    }));
  };

  const handleLineKeyDown = (e, index) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const state = lineSearch[index];
      if (!state?.search) return;

      if (state.results.length > 0) {
        selectProductForLine(index, state.results[0]);
      }
      // Si no hay resultados, no hace nada (el usuario verá lista vacía)
    }
  };

  const selectProductForLine = (index, product) => {
    updateDetail(index, "product_id", product.id);
    setLineSearch(prev => ({
      ...prev,
      [index]: { search: product.nombre, results: [] }
    }));
  };

  /* =========================
     HELPERS PARA PRESENTACIONES
  ========================== */
  const tienePresentacionesVariante = (detail) => {
    if (!detail.productData?.presentations) return false;
    return detail.productData.presentations.some(p => p.tipo_presentacion === 'variante');
  };

  const getPresentacionNombre = (detail) => {
    if (!detail.presentation_id || !detail.productData?.presentations) return "";
    const pres = detail.productData.presentations.find(p => p.id == detail.presentation_id);
    return pres ? pres.descripcion : "";
  };

  /* =========================
     CÁLCULOS
  ========================== */
  const calcularSubtotalLinea = (detail) => {
    return Number(detail.precio_unitario || 0) * Number(detail.cantidad || 0);
  };

  const subtotalGeneral = details.reduce((sum, d) => sum + calcularSubtotalLinea(d), 0);
  const ivaTotal = subtotalGeneral * 0.13;
  const montoFinal = subtotalGeneral + ivaTotal;

  /* =========================
     ACTIONS
  ========================== */
  const resetForm = () => {
    setSupplierId("");
    setFechaCompra("");
    setNumeroFactura("");
    setDetails([]);
    setLineSearch({});
  };

  const handleCancel = () => {
    resetForm();
    onCancel?.();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!supplierId || !fechaCompra || !numeroFactura || details.length === 0) {
      Swal.fire("Error", "Complete todos los campos", "error");
      return;
    }

    for (let i = 0; i < details.length; i++) {
      const detail = details[i];
      if (!detail.product_id) {
        Swal.fire("Error", `Seleccione un producto en la línea ${i + 1}`, "error");
        return;
      }
      if (tienePresentacionesVariante(detail) && !detail.presentation_id) {
        Swal.fire(
          "Error",
          `Debe seleccionar una presentación para ${detail.productData.nombre}`,
          "error"
        );
        return;
      }
    }

    const payload = {
      supplier_id: supplierId,
      fecha_compra: fechaCompra,
      numero_factura_proveedor: numeroFactura,
      details: details.map((d) => ({
        product_id: d.product_id,
        presentation_id: d.presentation_id || null,
        cantidad: Number(d.cantidad),
        precio_unitario: Number(d.precio_unitario),
      })),
    };

    try {
      await api.post("/purchases", payload);
      Swal.fire("Guardado", "Compra registrada correctamente", "success");
      resetForm();
      onSaved?.();
    } catch (error) {
      Swal.fire(
        "Error",
        error.response?.data?.message || "Error al guardar la compra",
        "error"
      );
    }
  };

  /* =========================
     UI
  ========================== */
  return (
    <div className="card mb-4">
      <div className="card-header">
        <div className="d-flex align-items-center gap-2">
          <Package size={24} color="#667eea" />
          <h2 className="card-title">Nueva Compra</h2>
        </div>
      </div>

      <div className="card-body">
        <form onSubmit={handleSubmit}>
          {/* DATOS GENERALES */}
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: "16px"
          }} className="mb-4">
            <div className="form-group">
              <label className="form-label">Proveedor *</label>
              <select
                className="form-control"
                value={supplierId}
                onChange={(e) => setSupplierId(e.target.value)}
                required
              >
                <option value="">Seleccione un proveedor</option>
                {suppliers.map((s) => (
                  <option key={s.id} value={s.id}>{s.nombre}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Fecha de Compra *</label>
              <input
                type="date"
                className="form-control"
                value={fechaCompra}
                onChange={(e) => setFechaCompra(e.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">N° Factura Proveedor *</label>
              <input
                type="text"
                className="form-control"
                placeholder="Ej: FAC-001-2025"
                value={numeroFactura}
                onChange={(e) => setNumeroFactura(e.target.value)}
                required
              />
            </div>
          </div>

          <div style={{ borderTop: "2px solid #e2e8f0", margin: "24px 0" }}></div>

          {/* PRODUCTOS */}
          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h4 style={{ fontSize: "16px", fontWeight: 600, margin: 0 }}>Productos</h4>
              <button type="button" className="btn btn-primary btn-sm" onClick={addDetail}>
                <PlusCircle size={16} />
                Agregar Producto
              </button>
            </div>

            {details.length === 0 ? (
              <div className="alert alert-info text-center">
                No hay productos agregados. Haz clic en "Agregar Producto" para comenzar.
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                {details.map((d, index) => {
                  const ls = lineSearch[index] || { search: "", results: [] };

                  return (
                    <div
                      key={index}
                      style={{
                        display: "grid",
                        gridTemplateColumns: tienePresentacionesVariante(d)
                          ? "2fr 1.5fr 1fr 1fr 1fr 60px"
                          : "2fr 1fr 1fr 1fr 60px",
                        gap: "12px",
                        padding: "12px",
                        background: "#f7fafc",
                        borderRadius: "8px",
                        alignItems: "end",
                      }}
                    >
                      {/* PRODUCTO — input con búsqueda */}
                      <div className="form-group" style={{ marginBottom: 0, position: "relative" }}>
                        <label className="form-label" style={{ fontSize: "12px" }}>
                          Producto *
                        </label>
                        <input
                          ref={el => inputRefs.current[index] = el}
                          type="text"
                          className="form-control"
                          placeholder="Escanear o buscar producto..."
                          value={ls.search}
                          onChange={(e) => handleLineSearchChange(index, e.target.value)}
                          onKeyDown={(e) => handleLineKeyDown(e, index)}
                          autoComplete="off"
                          style={{
                            borderColor: d.product_id ? "#48bb78" : undefined,
                            fontSize: "13px"
                          }}
                        />

                        {/* DROPDOWN de resultados */}
                        {ls.results.length > 0 && (
                          <div
                            className="card"
                            style={{
                              position: "absolute",
                              top: "100%",
                              left: 0,
                              right: 0,
                              zIndex: 1000,
                              maxHeight: "200px",
                              overflowY: "auto",
                              marginTop: "2px",
                              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                            }}
                          >
                            {ls.results.map(p => (
                              <div
                                key={p.id}
                                onMouseDown={() => selectProductForLine(index, p)}
                                style={{
                                  padding: "8px 14px",
                                  cursor: "pointer",
                                  borderBottom: "1px solid #e2e8f0",
                                  fontSize: "13px",
                                }}
                                onMouseEnter={e => e.currentTarget.style.background = "#f0f4ff"}
                                onMouseLeave={e => e.currentTarget.style.background = "white"}
                              >
                                <strong style={{ color: "var(--primary-color, #667eea)" }}>
                                  {p.codigo}
                                </strong>
                                {" — "}
                                <span>{p.nombre}</span>
                                {p.presentations?.some(pr => pr.tipo_presentacion === 'variante') && " 🎨"}
                                {p.presentations?.some(pr => pr.tipo_presentacion === 'empaque') && " 📦"}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* PRESENTACIÓN */}
                      {tienePresentacionesVariante(d) && (
                        <div className="form-group" style={{ marginBottom: 0 }}>
                          <label className="form-label" style={{ fontSize: "12px", display: "flex", alignItems: "center", gap: "4px" }}>
                            <Layers size={12} />
                            Presentación *
                          </label>
                          <select
                            className="form-control"
                            value={d.presentation_id || ""}
                            onChange={(e) => updateDetail(index, "presentation_id", e.target.value)}
                            required
                            style={{ borderColor: "#805ad5" }}
                          >
                            <option value="">Seleccionar variante</option>
                            {d.productData.presentations
                              .filter(pres => pres.tipo_presentacion === 'variante')
                              .map((pres) => (
                                <option key={pres.id} value={pres.id}>
                                  {pres.descripcion}{` (Stock: ${pres.stock_actual || 0})`}
                                </option>
                              ))}
                          </select>
                        </div>
                      )}

                      {/* CANTIDAD */}
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label className="form-label" style={{ fontSize: "12px" }}>Cantidad *</label>
                        <input
                          type="number"
                          className="form-control"
                          min="1"
                          value={d.cantidad}
                          onChange={(e) => updateDetail(index, "cantidad", e.target.value)}
                          required
                        />
                      </div>

                      {/* PRECIO UNITARIO */}
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label className="form-label" style={{ fontSize: "12px" }}>Precio Unitario (sin IVA) *</label>
                        <input
                          type="number"
                          className="form-control"
                          step="0.01"
                          min="0"
                          placeholder="0.00"
                          value={d.precio_unitario}
                          onChange={(e) => updateDetail(index, "precio_unitario", e.target.value)}
                          required
                        />
                      </div>

                      {/* SUBTOTAL */}
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label className="form-label" style={{ fontSize: "12px" }}>Subtotal</label>
                        <input
                          type="text"
                          className="form-control"
                          value={`$${calcularSubtotalLinea(d).toFixed(2)}`}
                          disabled
                          style={{ background: "#e2e8f0", fontWeight: 600, color: "#2d3748" }}
                        />
                      </div>

                      {/* ELIMINAR */}
                      <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        onClick={() => removeDetail(index)}
                        style={{ height: "46px" }}
                        title="Eliminar producto"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div style={{ borderTop: "2px solid #e2e8f0", margin: "24px 0" }}></div>

          {/* TOTALES */}
          <div style={{ background: "#f7fafc", padding: "20px", borderRadius: "10px" }} className="mb-4">
            <div style={{ display: "flex", flexDirection: "column", gap: "12px", maxWidth: "400px", marginLeft: "auto" }}>
              <div className="d-flex justify-content-between align-items-center">
                <span style={{ fontSize: "16px", fontWeight: 500 }}>Subtotal (sin IVA):</span>
                <span style={{ fontSize: "16px", fontWeight: 600 }}>${subtotalGeneral.toFixed(2)}</span>
              </div>
              <div className="d-flex justify-content-between align-items-center">
                <span style={{ fontSize: "16px", fontWeight: 500 }}>IVA (13%):</span>
                <span style={{ fontSize: "16px", fontWeight: 600 }}>${ivaTotal.toFixed(2)}</span>
              </div>
              <div style={{ borderTop: "2px solid #cbd5e0", margin: "4px 0" }}></div>
              <div className="d-flex justify-content-between align-items-center">
                <span style={{ fontSize: "18px", fontWeight: 700 }}>Total de la Compra:</span>
                <span style={{ fontSize: "20px", fontWeight: 700, color: "#48bb78" }}>${montoFinal.toFixed(2)}</span>
              </div>
            </div>
            <small style={{ color: "#718096", display: "block", marginTop: "16px", fontStyle: "italic" }}>
              * Los precios unitarios deben ingresarse sin IVA según factura de crédito fiscal
            </small>
          </div>

          {/* BOTONES */}
          <div className="d-flex gap-2">
            <button type="submit" className="btn btn-success">
              <Save size={18} />
              Guardar Compra
            </button>
            <button type="button" className="btn btn-secondary" onClick={handleCancel}>
              <X size={18} />
              Cancelar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}